import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import Notification from '../components/Notification';
import { CityContext } from '../contexts/CityContext';
import { UserContext } from '../contexts/UserContext';
import '../styles/register.css';

const CompleteProfile = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { cities = [], isLoading: citiesLoading } = useContext(CityContext) || {};
    const { redirectToLogin } = useContext(UserContext);
    
    // Get data from navigation state
    const { first_name, last_name, email, socialAuth, socialToken } = location.state || {};

    const [formData, setFormData] = useState({
        first_name: first_name || '',
        last_name: last_name || '',
        mobile_number: '',
        city: '',
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({
        message: '',
        type: 'info',
        header: ''
    });
    const [errors, setErrors] = useState({});
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [isCheckingPhone, setIsCheckingPhone] = useState(false);
    const [phoneExists, setPhoneExists] = useState(false);

    // Redirect if no social auth data
    useEffect(() => {
        if (!socialAuth || !email) {
            navigate('/register');
        }
    }, [socialAuth, email, navigate]);

    const checkPhoneExists = async (phone) => {
        if (!validatePhone(phone)) {
            return false;
        }

        setIsCheckingPhone(true);
        try {
            const phoneNumber = `+243${phone}`;
            const response = await axios.post(`${config.backendUrl}/users/check-mobile-number/`, {
                mobile_number: phoneNumber
            });

            if (response.data.exists) {
                setErrors(prev => ({
                    ...prev,
                    mobile_number: 'Ce numéro est déjà utilisé'
                }));
                setPhoneExists(true);
                return true;
            } else {
                setPhoneExists(false);
                return false;
            }
        } catch (error) {
            console.error('Error checking phone:', error);
            return false;
        } finally {
            setIsCheckingPhone(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => {
                const { [name]: removed, ...rest } = prev;
                return rest;
            });
        }

        if (name === 'mobile_number' && value.length === 9) {
            checkPhoneExists(value);
        } else if (name === 'mobile_number') {
            setPhoneExists(false);
        }
    };

    const validatePhone = (phone) => {
        const cleanNumber = phone.replace(/[\s-]/g, '');
        const phoneRegex = /^[0-9]{9}$/;
        return phoneRegex.test(cleanNumber);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.mobile_number) {
            newErrors.mobile_number = 'Numéro de téléphone requis';
        } else if (!validatePhone(formData.mobile_number)) {
            newErrors.mobile_number = 'Numéro invalide (9 chiffres)';
        } else if (phoneExists) {
            newErrors.mobile_number = 'Ce numéro est déjà utilisé';
        }
        if (!formData.city) {
            newErrors.city = 'Ville requise';
        }
        if (!agreedToTerms) {
            newErrors.terms = 'Vous devez accepter les termes et conditions';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            const phoneNumber = `+243${formData.mobile_number}`;

            const requestBody = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                mobile_number: phoneNumber,
                city_id: formData.city,
                email: email,
                auth_provider: socialAuth,
            };

            // Add the appropriate token based on provider
            if (socialAuth === 'google') {
                requestBody.token = socialToken;
            } else if (socialAuth === 'apple') {
                requestBody.id_token = socialToken;
            } else if (socialAuth === 'facebook') {
                requestBody.access_token = socialToken;
            }

            const response = await axios.post(`${config.backendUrl}/users/complete-social-signup/`, requestBody);

            if (response.data && response.data.user_id) {
                setNotification({
                    message: 'Inscription réussie! Vérifiez votre téléphone pour le code de vérification.',
                    type: 'success',
                    header: 'Succès'
                });

                setTimeout(() => {
                    navigate(`/verify-user/${response.data.user_id}`);
                }, 2000);
            } else {
                setNotification({
                    message: 'Erreur inattendue lors de l\'inscription',
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } catch (error) {
            console.error('Registration error:', error);
            
            // Check if email already exists
            if (error.response?.status === 409 && error.response?.data?.email_exists) {
                setNotification({
                    message: 'Un compte existe déjà avec cet email. Veuillez vous connecter ou utiliser "Mot de passe oublié".',
                    type: 'warning',
                    header: 'Compte existant'
                });
                // Redirect to login after a delay
                setTimeout(() => {
                    redirectToLogin('/login');
                }, 3000);
            } else {
                setNotification({
                    message: error.response?.data?.error || 'Erreur lors de l\'inscription',
                    type: 'error',
                    header: 'Erreur'
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modern-auth-page">
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification({ message: '', type: 'info', header: '' })}
                />
            )}

            <div className="register-card" style={{ maxWidth: '500px', background: 'white', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', padding: '40px' }}>
                <h2 style={{ textAlign: 'center', marginBottom: '10px', color: '#333' }}>Complétez votre profil</h2>
                <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px', fontSize: '14px' }}>
                    Bienvenue {first_name}! Quelques informations supplémentaires pour finaliser votre inscription.
                </p>

                {/* Progress Indicator */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                    {[1, 2, 3].map(step => {
                        const getStepColor = (stepNum) => {
                            if (stepNum === 1) return '#c51618';
                            if (stepNum === 2) return '#FFC107';
                            if (stepNum === 3) return '#003366';
                            return '#e0e0e0';
                        };
                        const isCompleted = step < 3;
                        const isActive = step === 3;
                        return (
                            <div key={step} style={{ flex: 1, marginRight: step < 3 ? '10px' : '0' }}>
                                <div style={{
                                    height: '4px',
                                    backgroundColor: isCompleted || isActive ? getStepColor(step) : '#e0e0e0',
                                    borderRadius: '2px',
                                    transition: 'background-color 0.3s'
                                }} />
                                <p style={{
                                    textAlign: 'center',
                                    fontSize: '12px',
                                    marginTop: '5px',
                                    color: isCompleted || isActive ? getStepColor(step) : '#999'
                                }}>
                                    Étape {step}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Step 1 - Completed (shown but disabled) */}
                    <div style={{ marginBottom: '20px', opacity: 0.6 }}>
                        <h3 style={{ marginBottom: '15px', color: '#c51618', fontSize: '16px' }}>✓ Informations personnelles</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>Prénom</label>
                                <input
                                    type="text"
                                    value={formData.first_name}
                                    disabled
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '14px' }}>Nom</label>
                                <input
                                    type="text"
                                    value={formData.last_name}
                                    disabled
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        backgroundColor: '#f5f5f5'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Step 2 - Completed (shown as placeholder) */}
                    <div style={{ marginBottom: '20px', opacity: 0.6 }}>
                        <h3 style={{ marginBottom: '15px', color: '#FFC107', fontSize: '16px' }}>✓ Contact et localisation</h3>
                        <p style={{ fontSize: '14px', color: '#666' }}>À compléter ci-dessous</p>
                    </div>

                    {/* Step 3 - Active */}
                    <div>
                        <h3 style={{ marginBottom: '15px', color: '#003366', fontSize: '16px' }}>Finalisation</h3>
                        
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Numéro de téléphone *</label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ padding: '10px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRight: 'none', borderRadius: '4px 0 0 4px' }}>+243</span>
                                <input
                                    type="text"
                                    name="mobile_number"
                                    value={formData.mobile_number}
                                    onChange={handleChange}
                                    placeholder="999999999"
                                    maxLength={9}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        border: errors.mobile_number ? '1px solid red' : '1px solid #ddd',
                                        borderRadius: '0 4px 4px 0',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                            {errors.mobile_number && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.mobile_number}</p>}
                            {isCheckingPhone && <p style={{ color: '#666', fontSize: '12px', marginTop: '5px' }}>Vérification...</p>}
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Ville *</label>
                            <select
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: errors.city ? '1px solid red' : '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="">Sélectionnez une ville</option>
                                {cities.map(city => (
                                    <option key={city.id} value={city.id}>{city.name}</option>
                                ))}
                            </select>
                            {errors.city && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.city}</p>}
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'start', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    style={{ marginRight: '10px', marginTop: '4px' }}
                                />
                                <span style={{ fontSize: '14px' }}>
                                    J'accepte les <a href="/terms-conditions/conditions-utilisation" target="_blank" style={{ color: '#003366' }}>termes et conditions</a> et la <a href="/terms-conditions/politique-confidentialite" target="_blank" style={{ color: '#003366' }}>politique de confidentialité</a>
                                </span>
                            </label>
                            {errors.terms && <p style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.terms}</p>}
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting || !formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone || !agreedToTerms}
                            style={{
                                width: '100%',
                                padding: '12px',
                                backgroundColor: (isSubmitting || !formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone || !agreedToTerms) ? '#ccc' : '#003366',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '16px',
                                fontWeight: '500',
                                cursor: (isSubmitting || !formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone || !agreedToTerms) ? 'not-allowed' : 'pointer',
                                opacity: (isSubmitting || !formData.mobile_number || !formData.city || !validatePhone(formData.mobile_number) || phoneExists || isCheckingPhone || !agreedToTerms) ? 0.6 : 1
                            }}
                        >
                            {isSubmitting ? 'Inscription...' : 'S\'inscrire'}
                        </button>
                    </div>
                </form>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <p style={{ fontSize: '14px', color: '#666' }}>
                        Vous avez déjà un compte? <a href="/login" style={{ color: '#003366', textDecoration: 'none' }}>Connexion</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CompleteProfile;
